import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider";
import { Notice, Progress } from "../components/Notice";
import { api, ApiError, type DealerMe } from "../lib/api";
import { DealerApprovals } from "./DealerApprovals";

type State =
  | { kind: "loading" }
  | { kind: "dealer"; me: DealerMe }
  | { kind: "not_a_dealer" }
  | { kind: "unavailable" }
  | { kind: "error"; message: string; retryable: boolean };

/**
 * The dealer area. Protected twice: the route needs a session, and the API
 * answers 403 "not_a_dealer" unless the operator granted this SoundLayer ID
 * a dealer row. A client account sees a plain explanation, never dealer data.
 */
export function Dealer() {
  const { session, signOut } = useAuth();
  const [state, setState] = useState<State>({ kind: "loading" });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let active = true;
    setState({ kind: "loading" });
    api
      .dealerMe()
      .then((me) => active && setState({ kind: "dealer", me }))
      .catch((error: unknown) => {
        if (!active) return;
        if (error instanceof ApiError && error.status === 403 && error.code === "not_a_dealer") {
          setState({ kind: "not_a_dealer" });
        } else if (error instanceof ApiError && error.code === "dealer_unavailable") {
          setState({ kind: "unavailable" });
        } else {
          setState({
            kind: "error",
            message: error instanceof ApiError ? error.message : "SoundLayer could not be reached.",
            retryable: !(error instanceof ApiError && error.answered),
          });
        }
      });
    return () => {
      active = false;
    };
  }, [attempt]);

  const email = session?.user.email ?? null;

  if (state.kind === "loading") return <Progress label="Opening your dealer area…" />;

  if (state.kind === "unavailable") {
    return (
      <section className="stack" style={{ maxWidth: 520 }}>
        <div>
          <h1>Dealer area</h1>
          <p className="lede">The dealer area is not open yet.</p>
        </div>
        <Notice kind="info">
          Your SoundLayer ID is fine. Dealer access is being switched on; your library and account work as usual
          in the meantime.
        </Notice>
        <Link className="btn btn-primary" to="/">
          Go to my library
        </Link>
      </section>
    );
  }

  if (state.kind === "not_a_dealer") {
    return (
      <section className="stack" style={{ maxWidth: 520 }}>
        <div>
          <h1>Dealer area</h1>
          <p className="lede">This SoundLayer ID is not set up as a dealer account.</p>
        </div>
        <Notice kind="info" title={email ? `Signed in as ${email}` : "Signed in"}>
          Dealer access is granted by SoundLayer to the SoundLayer ID your business registered with. If that is
          a different ID, sign out and sign in with it. If you expected access on this ID, contact SoundLayer.
        </Notice>
        <div className="row" style={{ gap: 10, flexWrap: "wrap" }}>
          <Link className="btn btn-primary" to="/library">
            Go to my library
          </Link>
          <button className="btn" type="button" onClick={() => void signOut()}>
            Sign out
          </button>
        </div>
      </section>
    );
  }

  if (state.kind === "error") {
    return (
      <section className="stack" style={{ maxWidth: 520 }}>
        <h1>Dealer area</h1>
        <Notice kind="error" title="Your dealer area could not be loaded.">
          {state.message}
        </Notice>
        {state.retryable && (
          <button className="btn" type="button" onClick={() => setAttempt((n) => n + 1)}>
            Try again
          </button>
        )}
      </section>
    );
  }

  const { me } = state;
  return (
    <section className="stack">
      <div className="row spread" style={{ alignItems: "flex-start", gap: 16, flexWrap: "wrap" }}>
        <div>
          <h1>{me.integrator.name}</h1>
          <p className="lede" style={{ marginBottom: 0 }}>
            Dealer area · {me.role === "owner" ? "Owner" : "Staff"}
            {email ? ` · ${email}` : ""}
          </p>
        </div>
        <Link className="btn btn-quiet" to="/account">
          Account
        </Link>
      </div>

      {me.admin && <DealerApprovals />}

      <div className="card stack">
        <div className="row spread">
          <h3>Clients</h3>
          <span className="pill">{me.clients.length}</span>
        </div>
        {me.clients.length === 0 ? (
          <p className="muted small" style={{ margin: 0 }}>
            No clients are registered to {me.integrator.name} yet.
          </p>
        ) : (
          <ul className="list" aria-label="Clients">
            {me.clients.map((c) => (
              <li key={c.id} className="row spread">
                <span>{c.name}</span>
                <span className="muted small">{c.type ?? ""}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="card stack">
        <h3>Business</h3>
        <dl className="kv">
          <dt>Name</dt>
          <dd>{me.integrator.name}</dd>
          <dt>Contact</dt>
          <dd>{me.integrator.email}</dd>
        </dl>
        <p className="muted small" style={{ margin: 0 }}>
          Client onboarding, room presets and installation tools for dealers are on the way. Your client list and
          business details above are live.
        </p>
      </div>
    </section>
  );
}
